import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Loader2, ShieldCheck, ArrowRight, Linkedin, Instagram } from "lucide-react";
import ContactOTPStep from "@/components/zenoviq/ContactOTPStep";
import ContactSuccessStep from "@/components/zenoviq/ContactSuccessStep";
import { useContactForm } from "@/components/zenoviq/useContactForm";

const SERVICES_OPTIONS = [
  "Machine Learning / AI",
  "Data Analytics & Visualization",
  "SQL / Database Engineering",
  "Full-Stack Development",
  "Cloud & Infrastructure",
  "Financial Analysis",
  "Business Analysis",
  "Performance Reporting",
  "Forecasting & Strategy",
  "Brand Identity / Logo Design",
  "UI/UX Design",
  "Social Media Design",
  "Something else",
];

function Field({ label, required, testId, value, onChange, placeholder, type = "text", hint }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">
        {label} {required && <span className="text-blue-400">*</span>}
      </label>
      <input
        type={type}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-blue-500/60 focus:bg-white/[0.06] outline-none transition-colors"
      />
      {hint && <p className="mt-1.5 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

function ContactIntro() {
  return (
    <div className="lg:col-span-5">
      <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-400" data-testid="contact-overline">
        [ Contact ]
      </div>
      <h2
        className="mt-5 font-display text-4xl md:text-5xl font-medium tracking-tight"
        data-testid="contact-heading"
      >
        Let&apos;s Build Something
        <br />
        <span className="text-blue-400">Intelligent Together.</span>
      </h2>
      <p className="mt-6 text-zinc-400 text-base md:text-lg leading-relaxed">
        Whether it&apos;s data, design, finance, or technology — we&apos;re ready to help transform your ideas into impactful solutions.
      </p>

      <div className="mt-12 space-y-5">
        <InfoRow icon={ShieldCheck} title="Email-verified contact" body="We send a one-time 6-digit code to confirm your inbox before delivering your message." />
        <InfoRow icon={Mail} title="Reply within 1–2 business days" body="You'll get a personal reply with proposed next steps and a free 30-minute scoping call." />
      </div>

      <div className="mt-12">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">Or find us on</div>
        <div className="flex gap-3">
          <SocialIcon href="https://www.linkedin.com/company/dataviqu/" Icon={Linkedin} testId="contact-linkedin" label="LinkedIn" />
          <SocialIcon href="https://www.instagram.com/dataviqu.official?igsh=MTNpamFsaXZxNTY4cw%3D%3D&utm_source=qr" Icon={Instagram} testId="contact-instagram" label="Instagram" />
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl border border-blue-500/30 bg-blue-500/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-blue-400" />
      </div>
      <div>
        <div className="text-sm text-white font-medium">{title}</div>
        <div className="text-sm text-zinc-500 mt-1">{body}</div>
      </div>
    </div>
  );
}

function SocialIcon({ href, Icon, testId, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/5 hover:border-blue-500/40 transition-colors"
      aria-label={label}
    >
      <Icon className="w-4 h-4 text-zinc-300" />
    </a>
  );
}

function ContactFormStep({ form, updateField, isFormValid, loading, onSubmit }) {
  return (
    <motion.form
      key="form-step"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.35 }}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-5"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field
          label="Full Name"
          required
          testId="contact-input-full-name"
          value={form.full_name}
          onChange={(v) => updateField("full_name", v)}
          placeholder="Jane Doe"
        />
        <Field
          label="Phone Number"
          required
          testId="contact-input-phone"
          value={form.phone_number}
          onChange={(v) => updateField("phone_number", v)}
          placeholder="+1 555 000 1234"
        />
      </div>

      <Field
        label="Email Address"
        type="email"
        required
        testId="contact-input-email"
        value={form.email}
        onChange={(v) => updateField("email", v)}
        placeholder="you@company.com"
        hint="We'll send a 6-digit verification code here."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Services Required <span className="text-blue-400">*</span>
          </label>
          <select
            data-testid="contact-input-services"
            value={form.services_required}
            onChange={(e) => updateField("services_required", e.target.value)}
            className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-blue-500/60 focus:bg-white/[0.06] outline-none transition-colors"
          >
            <option value="" className="bg-[#0A0A0A]">Select a service</option>
            {SERVICES_OPTIONS.map((s) => (
              <option key={s} value={s} className="bg-[#0A0A0A]">{s}</option>
            ))}
          </select>
        </div>
        <Field
          label="Company Name"
          testId="contact-input-company"
          value={form.company_name}
          onChange={(v) => updateField("company_name", v)}
          placeholder="Acme Inc."
        />
      </div>

      <div>
        <label className="block text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">
          Your Message <span className="text-blue-400">*</span>
        </label>
        <textarea
          data-testid="contact-input-message"
          value={form.message}
          onChange={(e) => updateField("message", e.target.value)}
          rows={5}
          placeholder="Tell us about your project, goals, and timeline…"
          className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-blue-500/60 focus:bg-white/[0.06] outline-none transition-colors resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={loading || !isFormValid}
        data-testid="contact-send-otp-button"
        className="group w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-100 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending verification code…
          </>
        ) : (
          <>
            Verify email & continue
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </button>
      <p className="text-xs text-zinc-500 text-center">
        We&apos;ll email you a one-time code to confirm your address before delivering your message.
      </p>
    </motion.form>
  );
}

export default function Contact() {
  const {
    form, otp, setOtp, step, setStep, loading, isFormValid,
    updateField, sendOTP, verifyAndSubmit, resetAll,
  } = useContactForm();

  const stepLabel = step === "form" ? "01 · Details" : step === "otp" ? "02 · Verify" : "03 · Sent";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(37,99,235,0.12) 0%, rgba(5,5,5,0) 60%)" }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <ContactIntro />

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-10"
            data-testid="contact-card"
          >
            <div className="flex items-baseline justify-between mb-8">
              <h3 className="font-display text-2xl font-medium tracking-tight" data-testid="contact-form-title">
                Discuss Your Project
              </h3>
              <div className="text-xs font-mono text-zinc-500">{stepLabel}</div>
            </div>

            <AnimatePresence mode="wait">
              {step === "form" && (
                <ContactFormStep
                  form={form}
                  updateField={updateField}
                  isFormValid={isFormValid}
                  loading={loading}
                  onSubmit={sendOTP}
                />
              )}
              {step === "otp" && (
                <ContactOTPStep
                  email={form.email}
                  otp={otp}
                  setOtp={setOtp}
                  loading={loading}
                  onVerify={verifyAndSubmit}
                  onResend={sendOTP}
                  onBack={() => setStep("form")}
                />
              )}
              {step === "success" && <ContactSuccessStep onReset={resetAll} />}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
